import {
  useCallback,
  ChangeEvent,
  DragEvent,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import MainLayout from 'views/layouts/Main'
import Stepper from 'views/components/Stepper'
import { H2, H3, H4, P } from 'views/components/UI/Text'
import {
  Button,
  BackButton,
  StrokedButton,
} from 'views/components/UI/Buttons'
import Modal from 'views/components/UI/Modal'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import {
  setStepFour,
  setStepThree,
  setStepTwo,
  setStepTwoTrigger,
  stepOneSelector,
} from 'ducks/createProcess'
import { platformAdminRoutes } from 'router/routes'
import { STEPS_NEW_PROCESS } from '../constants'
import { useNotification } from 'lib/context/notification.context'
import fetchIdealProfilePreview, {
  LuminaIdealProfilePreviewResponse,
} from 'services/ai/fetchIdealProfilePreview'
import {
  createProcessService,
  getProcessDetail,
} from 'services/common/process/createProcess/create-process-service'
import { generalDataSelector } from 'ducks/auth'
import { usePreventNavigation } from 'views/screens/super-admin/homologation-skills/hooks/usePreventNavigation'
import {
  Card,
  Checkbox,
  Container,
  Content,
  EditorGrid,
  EditorLabel,
  FileInput,
  Footer,
  HeaderContainer,
  InfoBox,
  Input,
  MutedText,
  RecommendationCard,
  RecommendationGrid,
  SearchEmptyState,
  SearchResultButton,
  SearchResults,
  SearchableSelectWrap,
  RoleSummary,
  QuadrantBadge,
  QuadrantHeader,
  QuadrantName,
  SectionDivider,
  Select,
  StatusPill,
  StyledTable,
  CounterPill,
  InlineActions,
  RemoveButton,
  SmallButton,
  TableToolbar,
  TableWrap,
  Tag,
  TagList,
  TestHeader,
  ToggleRow,
  UploadBox,
  ValidationBox,
} from './styled'

type Quadrant = 'FI' | 'FD' | 'BI' | 'BD'
type QuadrantRole =
  | 'dominante'
  | 'auxiliar_1'
  | 'auxiliar_2'
  | 'menos_eficiente'

type PreviewData = NonNullable<
  LuminaIdealProfilePreviewResponse['data']
>

type BetesaDraft = Record<
  Quadrant,
  {
    role: QuadrantRole
    min: number | ''
    max: number | ''
  }
>

type CompetenceDraft = Omit<
  PreviewData['competences_ideal_profile'][number],
  'weight'
> & {
  enabled: boolean
  weight: number | ''
}

const QUADRANTS: Quadrant[] = ['FI', 'FD', 'BI', 'BD']

const ALERT_RANGE_IDS: Record<string, number> = {
  E: 1,
  EI: 2,
  EEII: 3,
  IE: 4,
  I: 5,
}

const ALERT_RANGE_OPTIONS = [
  { value: 'E|EI', label: 'E - EI' },
  { value: 'E|EEII', label: 'E - EEII' },
  { value: 'E|IE', label: 'E - IE' },
  { value: 'E|I', label: 'E - I' },
  { value: 'EI|EEII', label: 'EI - EEII' },
  { value: 'EI|IE', label: 'EI - IE' },
  { value: 'EI|I', label: 'EI - I' },
  { value: 'EEII|IE', label: 'EEII - IE' },
  { value: 'EEII|I', label: 'EEII - I' },
  { value: 'IE|I', label: 'IE - I' },
]

const MIN_SKILLS = 3
const MAX_SKILLS = 10

const sortedNumericValues = (values: Array<number | string>) =>
  values
    .map((value) => Number(value))
    .filter((value) => !Number.isNaN(value))
    .sort((a, b) => a - b)

const haveSameNumericValues = (
  left: Array<number | string>,
  right: Array<number | string>,
) => {
  const normalizedLeft = sortedNumericValues(left)
  const normalizedRight = sortedNumericValues(right)

  if (normalizedLeft.length !== normalizedRight.length) {
    return false
  }

  return normalizedLeft.every(
    (value, index) => value === normalizedRight[index],
  )
}

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

const QUADRANT_ROLE_LABELS: Record<QuadrantRole, string> = {
  dominante: 'Dominante',
  auxiliar_1: 'Auxiliar 1',
  auxiliar_2: 'Auxiliar 2',
  menos_eficiente: 'Menos eficiente',
}

const buildPriorityWeights = (level: string | undefined, count: number) => {
  const safeCount = Math.max(MIN_SKILLS, Math.min(MAX_SKILLS, count))
  const normalized = (level || '').toLowerCase()

  if (normalized.includes('estrat')) {
    const strongCount = Math.max(4, Math.round(safeCount * 0.5))
    const over90 = Math.min(2, strongCount)
    const values = [92, 90].slice(0, over90)
    values.push(...[88, 85, 82, 80].slice(0, strongCount - over90))
    while (values.length < safeCount) {
      values.push([70, 68, 66, 64, 62, 60][values.length - strongCount] || 60)
    }
    return values.slice(0, safeCount)
  }

  if (normalized.includes('tact')) {
    const strongCount = safeCount >= 6 ? 3 : 2
    const values = [90, 87, 85].slice(0, strongCount)
    while (values.length < safeCount) {
      values.push([70, 68, 66, 64, 62, 60][values.length - strongCount] || 60)
    }
    return values.slice(0, safeCount)
  }

  const strongCount = safeCount >= 5 ? 2 : 1
  const values = [85, 82].slice(0, strongCount)
  while (values.length < safeCount) {
    values.push([70, 68, 66, 64, 62, 60][values.length - strongCount] || 60)
  }
  return values.slice(0, safeCount)
}

const normalizeCompetencePriorities = (
  items: CompetenceDraft[],
  level?: string,
) : CompetenceDraft[] => {
  const enabled = items
    .filter((item) => item.enabled)
    .sort((a, b) => Number(a.priority) - Number(b.priority))
    .map((item, index): CompetenceDraft => ({
      ...item,
      priority: index + 1,
    }))

  const weights = buildPriorityWeights(level, enabled.length)
  const enabledWithWeights = enabled.map(
    (item, index): CompetenceDraft => ({
      ...item,
      weight: item.weight === '' ? '' : weights[index],
    }),
  )

  const disabled = items.filter((item) => !item.enabled)
  return [...enabledWithWeights, ...disabled]
}

const buildBetesaDraft = (preview: PreviewData): BetesaDraft => {
  const structure = preview.betesa_ideal_profile?.structure || {}
  const ranges = preview.betesa_ideal_profile?.profile_values || []

  const roleMap: Record<string, QuadrantRole> = {
    [structure.dominante || '']: 'dominante',
    [structure.auxiliares?.[0] || '']: 'auxiliar_1',
    [structure.auxiliares?.[1] || '']: 'auxiliar_2',
    [structure.menos_eficiente || '']: 'menos_eficiente',
  }

  return QUADRANTS.reduce((acc, quadrant) => {
    const foundRange = ranges.find((item) => item.quadrant === quadrant)
    acc[quadrant] = {
      role: roleMap[quadrant] || 'auxiliar_2',
      min: foundRange?.min ?? 50,
      max: foundRange?.max ?? 79,
    }
    return acc
  }, {} as BetesaDraft)
}

const hasIncompleteBetesaDraft = (draft: BetesaDraft | null) => {
  if (!draft) return true

  return QUADRANTS.some((quadrant) => {
    const current = draft[quadrant]
    return current.min === '' || current.max === ''
  })
}

const hasIncompleteCompetenceWeights = (items: CompetenceDraft[]) =>
  items.some((item) => item.enabled && item.weight === '')

const buildRoleName = (preview: PreviewData, processName?: string) => {
  const rawRole =
    preview.role.nombre_rol || preview.validation.detected?.nombre_rol || ''
  const compactRole = rawRole.replace(/\s+/g, ' ').trim()

  if (!compactRole || compactRole.length > 120) {
    return processName || 'Rol detectado'
  }

  return compactRole
}

const buildRoleSummary = (preview: PreviewData) => {
  const excerpt = (preview.pdf_excerpt || '').replace(/\s+/g, ' ').trim()
  if (!excerpt) {
    return 'No se encontró un resumen legible del PDF. Puedes continuar ajustando la sugerencia manualmente.'
  }
  return excerpt.length > 420 ? `${excerpt.slice(0, 420)}...` : excerpt
}

const buildDefaultRationales = (
  preview: PreviewData,
  roleName: string,
) => {
  const competencies =
    preview.role.competencias_clave?.join(', ') ||
    'competencias priorizadas del cargo'
  const demands =
    preview.role.demandas_entorno?.join(', ') || 'el contexto de exigencia del rol'
  const dominant =
    preview.betesa_ideal_profile?.structure?.dominante || 'FI'
  const auxiliaries = preview.betesa_ideal_profile?.structure?.auxiliares || []
  const lessEfficient =
    preview.betesa_ideal_profile?.structure?.menos_eficiente || 'BD'
  const alertRange =
    preview.betesa_ideal_profile?.alert_level_range?.join(' - ') || 'EI - IE'

  const quadrantGuides: Record<
    string,
    {
      title: string
      contribution: string
      naturalTalent: string
    }
  > = {
    FI: {
      title: 'Frontal Izquierdo',
      contribution:
        'aporta análisis estructurado, criterio, lectura de riesgos y decisiones basadas en evidencia.',
      naturalTalent:
        'Suele ser un talento natural valioso cuando el rol exige profundidad técnica, pensamiento lógico y foco en resultados.',
    },
    BI: {
      title: 'Basal Izquierdo',
      contribution:
        'aporta orden, control, trazabilidad, disciplina metodológica y consistencia en la ejecución.',
      naturalTalent:
        'Suele ser un talento natural importante cuando el rol necesita rigor, seguimiento y cumplimiento sostenido.',
    },
    BD: {
      title: 'Basal Derecho',
      contribution:
        'aporta sensibilidad relacional, servicio, lectura del impacto humano y capacidad para sostener vínculos.',
      naturalTalent:
        'Suele ser un talento natural útil cuando el rol depende mucho de cercanía, contención e influencia interpersonal.',
    },
    FD: {
      title: 'Frontal Derecho',
      contribution:
        'aporta visión, flexibilidad, pensamiento de escenarios y capacidad para conectar perspectivas distintas.',
      naturalTalent:
        'Suele ser un talento natural valioso cuando el rol necesita adaptación, innovación y mirada amplia.',
    },
  }

  const dominantGuide = quadrantGuides[dominant] || quadrantGuides.FI
  const auxiliaryText = auxiliaries
    .map((quadrant) => {
      const guide = quadrantGuides[quadrant]
      if (!guide) {
        return ''
      }
      return `${quadrant} (${guide.title}) ${guide.contribution}`
    })
    .filter(Boolean)
    .join(' ')

  const lessEfficientGuide =
    quadrantGuides[lessEfficient] || quadrantGuides.BD

  return {
    betesa:
      `Bluesite Lumina sugiere este perfil de Betesa para el rol ${roleName} ` +
      `porque el cargo necesita responder bien a demandas como ${demands}. ` +
      `En esta lectura, el cuadrante dominante sugerido es ${dominant} (${dominantGuide.title}), ` +
      `porque ${dominantGuide.contribution} ${dominantGuide.naturalTalent} ` +
      `Como apoyos naturales aparecen ${auxiliaries.join(' y ') || 'los cuadrantes auxiliares sugeridos'}, ` +
      `que complementan el perfil así: ${auxiliaryText} ` +
      `Esto le da al cliente una señal importante: no solo se busca a alguien que piense bien, ` +
      `sino a alguien cuya forma natural de procesar el trabajo se sostenga con menor desgaste en el tiempo. ` +
      `El cuadrante menos eficiente sugerido es ${lessEfficient} (${lessEfficientGuide.title}), ` +
      `lo que no significa incapacidad, sino que esa forma de operar suele requerir más esfuerzo y no debería ser la base principal del cargo. ` +
      `En nivel de alerta, el rango ${alertRange} sugiere un entorno con una combinación de estímulo, presión e interacción que mantenga a la persona activa sin sacarla de foco.`,
    skills:
      `Se recomienda 3D Skills porque el cargo requiere evidenciar ` +
      `y comparar competencias observables en el proceso. La ` +
      `selección inicial se construyó a partir de ${competencies}, ` +
      `y puede ajustarse para reflejar mejor el perfil del cargo.`,
  }
}

const buildSkillsRationale = (
  competencies: CompetenceDraft[],
  roleName: string,
  demands: string[] = [],
) => {
  if (!competencies.length) {
    return `Bluesite Lumina no tiene competencias activas para sustentar el perfil de 3DSkills del rol ${roleName}.`
  }

  const demandText = demands.length
    ? demands.join(', ')
    : 'las exigencias observables del cargo'

  const competencyDescriptions: Record<string, string> = {
    'Toma de Decisiones (Accountability)':
      'ayuda a balancear criterio técnico, oportunidad y responsabilidad sobre decisiones sensibles.',
    'Orientación a los Datos':
      'permite interpretar indicadores, alertas y patrones con rigor y trazabilidad.',
    'Orientación a Resultados':
      'asegura foco en impacto, cumplimiento y sostenibilidad del desempeño.',
    Influencia:
      'facilita movilizar acuerdos y recomendaciones con áreas que tienen prioridades distintas.',
    'Pensamiento Crítico':
      'fortalece el análisis de escenarios, supuestos y riesgos antes de actuar.',
    'Resolución de Problemas':
      'aporta capacidad para identificar causas raíz y plantear soluciones viables.',
    'Flexibilidad (Adaptabilidad)':
      'apoya la respuesta frente a cambios del entorno, nuevas señales y ajustes del negocio.',
    'Gestión Centrada en el Cliente':
      'mantiene presente el impacto de las decisiones en la experiencia y sostenibilidad de la relación.',
    'Comunicación Efectiva':
      'permite traducir hallazgos técnicos en mensajes claros y accionables para distintos públicos.',
    Planeación:
      'ayuda a ordenar prioridades, anticipar escenarios y sostener la ejecución.',
    'Toma de Decisiones':
      'ayuda a balancear criterio técnico, oportunidad y responsabilidad sobre decisiones sensibles.',
    'Habilidad de Ejecución':
      'aporta disciplina para llevar el análisis a acciones concretas y consistentes.',
  }

  const sortedCompetencies = competencies
    .sort((a, b) => a.priority - b.priority)
  const topCompetencies = sortedCompetencies
    .slice(0, 3)
    .map((item) => item.skill_name)
    .join(', ')

  const details = sortedCompetencies
    .map((item) => {
      const description =
        competencyDescriptions[item.skill_name] ||
        'refuerza una parte importante del desempeño esperado para este cargo.'

      return `${item.skill_name}: ${description} Mínimo esperado ${item.weight}.`
    })
    .join(' ')

  return (
    `Bluesite Lumina sugiere este perfil de 3DSkills para el rol ${roleName} ` +
    `porque el cargo necesita responder bien a ${demandText}. ` +
    `Por eso prioriza primero ${topCompetencies}, y complementa esa base con ` +
    `otras competencias que ayudan a sostener criterio, ejecución e influencia en el contexto real del rol. ` +
    `${details}`
  )
}

function IdealProfileGeneratorAi() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { getError, getSuccess, getWarning } = useNotification()
  const stepOneData = useSelector(stepOneSelector)
  const { data: generalData } = useSelector(generalDataSelector)

  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<PreviewData>()
  const [selectedTests, setSelectedTests] = useState({
    betesa: true,
    skills: true,
  })
  const [betesaDraft, setBetesaDraft] = useState<BetesaDraft | null>(null)
  const [alertRangeValue, setAlertRangeValue] = useState('EI|IE')
  const [competenceDrafts, setCompetenceDrafts] = useState<
    CompetenceDraft[]
  >([])
  const [rationales, setRationales] = useState({
    betesa: '',
    skills: '',
  })
  const [newSkillId, setNewSkillId] = useState('')
  const [skillSearch, setSkillSearch] = useState('')
  const [showSkillResults, setShowSkillResults] = useState(false)
  const [allowNavigation, setAllowNavigation] = useState(false)
  const [showExitModal, setShowExitModal] = useState(false)
  const [pendingRoute, setPendingRoute] = useState<string | null>(null)

  const levelName = useMemo(() => {
    const selectedLevel = generalData?.level?.find(
      (level: any) => level.id === stepOneData.level,
    )
    return selectedLevel?.name || null
  }, [generalData, stepOneData.level])

  const displayRoleName = useMemo(() => {
    if (!preview) {
      return ''
    }
    return buildRoleName(preview, stepOneData.processName)
  }, [preview, stepOneData.processName])

  const enabledCompetences = useMemo(
    () => competenceDrafts.filter((item) => item.enabled),
    [competenceDrafts],
  )

  const skillsRationale = useMemo(
    () =>
      buildSkillsRationale(
        enabledCompetences,
        displayRoleName || 'este rol',
        preview?.role.demandas_entorno || [],
      ),
    [displayRoleName, enabledCompetences, preview?.role.demandas_entorno],
  )

  const availableSkillsToAdd = useMemo(() => {
    const selectedIds = new Set(
      enabledCompetences
        .map((item) => item.skill_id)
        .filter(Boolean) as number[],
    )

    return (preview?.skills_catalog || []).filter(
      (item) => !selectedIds.has(item.id),
    )
  }, [enabledCompetences, preview])

  const filteredSkillsToAdd = useMemo(() => {
    const normalizedSearch = normalizeSearchText(skillSearch)

    if (!normalizedSearch) {
      return availableSkillsToAdd.slice(0, 8)
    }

    return availableSkillsToAdd
      .filter((item) =>
        normalizeSearchText(item.skill).includes(normalizedSearch),
      )
      .slice(0, 12)
  }, [availableSkillsToAdd, skillSearch])

  const shouldPreventExit = Boolean((file || preview) && !allowNavigation)

  const handleNavigationAttempt = useCallback(async () => {
    setAllowNavigation(true)
  }, [])

  const {
    isModalOpen: isNavigationModalOpen,
    handleClose: handleCloseNavigationModal,
    handleConfirm: handleConfirmNavigationModal,
  } = usePreventNavigation({
    shouldPrevent: shouldPreventExit,
    onNavigationAttempt: handleNavigationAttempt,
  })

  const handleProtectedNavigate = useCallback(
    (path: string) => {
      if (shouldPreventExit) {
        setPendingRoute(path)
        setShowExitModal(true)
        return
      }

      navigate(path)
    },
    [navigate, shouldPreventExit],
  )

  const handleCloseExitModal = () => {
    setShowExitModal(false)
    setPendingRoute(null)
  }

  const handleConfirmExitModal = () => {
    setAllowNavigation(true)
    setShowExitModal(false)
    if (pendingRoute) {
      navigate(pendingRoute)
    }
  }

  const handleFileSelected = (selectedFile: File | null) => {
    if (!selectedFile) {
      return
    }

    if (selectedFile.type !== 'application/pdf') {
      getWarning('Solo puedes cargar archivos en formato PDF')
      return
    }

    setFile(selectedFile)
  }

  const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(false)
    handleFileSelected(event.dataTransfer.files?.[0] || null)
  }

  const handleGenerate = async () => {
    if (!file) {
      getWarning('Primero debes cargar el PDF del perfil del cargo')
      return
    }

    setLoading(true)
    const response = await fetchIdealProfilePreview({
      file,
      processName: stepOneData.processName,
      processType: String(stepOneData.processType || ''),
      levelId: Number(stepOneData.level || 0),
      levelName,
    })
    setLoading(false)

    if (response.status !== 'OK' || !response.data) {
      getError(
        response.message ||
          'No fue posible construir la sugerencia de perfil ideal.',
      )
      return
    }

    const nextPreview = response.data
    const roleName = buildRoleName(nextPreview, stepOneData.processName)

    setPreview(nextPreview)
    setSelectedTests({ betesa: true, skills: true })
    setBetesaDraft(buildBetesaDraft(nextPreview))
    setAlertRangeValue(
      nextPreview.betesa_ideal_profile?.alert_level_range?.join('|') ||
        'EI|IE',
    )
    setCompetenceDrafts(
      normalizeCompetencePriorities(
        (nextPreview.competences_ideal_profile || []).map(
          (item): CompetenceDraft => ({
            ...item,
            enabled: true,
            weight: item.weight,
          }),
        ),
        nextPreview.role.nivel,
      ),
    )
    setRationales(buildDefaultRationales(nextPreview, roleName))

    if (!nextPreview.validation.ok) {
      getWarning(
        'Se generó una lectura preliminar, pero el PDF necesita más contexto para una sugerencia confiable.',
      )
      return
    }

    getSuccess('Sugerencia generada. Ahora puedes revisarla, editarla y aplicarla.')
  }

  const handleQuadrantChange = (
    quadrant: Quadrant,
    field: 'role' | 'min' | 'max',
    value: string,
  ) => {
    setBetesaDraft((current) => {
      if (!current) {
        return current
      }

      return {
        ...current,
        [quadrant]: {
          ...current[quadrant],
          [field]:
            field === 'role'
              ? value
              : value === ''
              ? ''
              : Number(value),
        },
      }
    })
  }

  const handleCompetenceChange = (
    index: number,
    field: 'priority' | 'weight',
    value: string,
  ) => {
    setCompetenceDrafts((current) => {
      const updated = current.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [field]:
                field === 'weight' && value === ''
                  ? ''
                  : Number(value),
            }
          : item,
      )

      if (field === 'priority') {
        return normalizeCompetencePriorities(updated, preview?.role.nivel)
      }

      return updated
    })
  }

  const handleRemoveCompetence = (index: number) => {
    if (enabledCompetences.length <= MIN_SKILLS) {
      getWarning(
        `Debes mantener al menos ${MIN_SKILLS} competencias en 3D Skills.`,
      )
      return
    }

    setCompetenceDrafts((current) =>
      normalizeCompetencePriorities(
        current.map((item, itemIndex) =>
          itemIndex === index ? { ...item, enabled: false } : item,
        ),
        preview?.role.nivel,
      ),
    )
  }

  const handleAddCompetence = () => {
    if (!newSkillId) {
      getWarning('Selecciona una competencia para agregar.')
      return
    }

    if (enabledCompetences.length >= MAX_SKILLS) {
      getWarning(
        `No puedes superar ${MAX_SKILLS} competencias en 3D Skills.`,
      )
      return
    }

    const selectedCatalogItem = (preview?.skills_catalog || []).find(
      (item) => String(item.id) === newSkillId,
    )

    if (!selectedCatalogItem) {
      return
    }

    setCompetenceDrafts((current) =>
      normalizeCompetencePriorities(
        [
          ...current,
          {
            skill_id: selectedCatalogItem.id,
            skill_name: selectedCatalogItem.skill,
            matched_skill_name: selectedCatalogItem.skill,
            weight: 70,
            priority: enabledCompetences.length + 1,
            matched: true,
            enabled: true,
          } as CompetenceDraft,
        ],
        preview?.role.nivel,
      ),
    )
    setNewSkillId('')
    setSkillSearch('')
    setShowSkillResults(false)
  }

  const handleApply = async () => {
    if (!preview || !betesaDraft) {
      getWarning('Primero debes generar la sugerencia con IA')
      return
    }

    if (!selectedTests.betesa && !selectedTests.skills) {
      getWarning('Debes dejar al menos una prueba activa')
      return
    }

    if (
      selectedTests.skills &&
      (enabledCompetences.length < MIN_SKILLS ||
        enabledCompetences.length > MAX_SKILLS)
    ) {
      getWarning(
        `3D Skills debe quedar entre ${MIN_SKILLS} y ${MAX_SKILLS} competencias.`,
      )
      return
    }

    if (selectedTests.betesa && hasIncompleteBetesaDraft(betesaDraft)) {
      getWarning(
        'Completa los valores mínimos y máximos de Betesa antes de aplicar la sugerencia.',
      )
      return
    }

    if (
      selectedTests.skills &&
      hasIncompleteCompetenceWeights(enabledCompetences)
    ) {
      getWarning(
        'Completa los mínimos esperados de 3D Skills antes de aplicar la sugerencia.',
      )
      return
    }

    const roleAssignments = Object.entries(betesaDraft).reduce(
      (acc, [quadrant, config]) => {
        acc[config.role] = quadrant
        return acc
      },
      {} as Record<string, string>,
    )

    const builtBetesaProfile = QUADRANTS.map((quadrant) => {
      const config = betesaDraft[quadrant]
      const priority =
        config.role === 'dominante'
          ? 1
          : config.role === 'auxiliar_1'
          ? 2
          : config.role === 'auxiliar_2'
          ? 3
          : 4

      return {
        quadrant,
        priority,
        min: Number(config.min),
        max: Number(config.max),
      }
    })

    const alertLevelIds = alertRangeValue
      .split('|')
      .map((item) => ALERT_RANGE_IDS[item])
      .filter(Boolean)

    const selectedCompetences = enabledCompetences
      .filter((item) => item.skill_id)
      .map((item) => ({
        skillId: item.skill_id as number,
        skill_id: item.skill_id as number,
        weight: Number(item.weight),
      }))

    const stepTwoPayload = {
      processId: stepOneData.processId as number,
      isRoleFamilySelected: false,
      configurationMode: 'ai_ideal_profile' as const,
    }

    const stepThreePayload = {
      processId: stepOneData.processId,
      onlineCourse: false,
      useBetesaTest: selectedTests.betesa,
      useSkillsTest: selectedTests.skills,
      use360Test: false,
    }

    const stepFourPayload = {
      processId: stepOneData.processId,
      betesaIdealProfile: selectedTests.betesa
        ? {
            betesaSkills: [],
            alertLevelId: alertLevelIds,
            alert_level_range: alertLevelIds,
            profileValues: builtBetesaProfile,
            profile_values: builtBetesaProfile,
            structure: {
              dominante: roleAssignments.dominante,
              auxiliares: [
                roleAssignments.auxiliar_1,
                roleAssignments.auxiliar_2,
              ].filter(Boolean),
              menos_eficiente: roleAssignments.menos_eficiente,
            },
          }
        : undefined,
      competencesIdealProfile: selectedTests.skills
        ? selectedCompetences
        : [],
    }

    dispatch(setStepTwo(stepTwoPayload))
    dispatch(setStepThree(stepThreePayload))
    dispatch(setStepFour(stepFourPayload as any))
    setLoading(true)
    setAllowNavigation(true)

    try {
      const stepTwoResponse = await createProcessService({
        family_role: {
          process_id: stepOneData.processId as number,
          role_family: false,
        },
      })

      if (stepTwoResponse.status !== 'OK') {
        throw new Error(
          'No pudimos guardar el modo de configuración del proceso.',
        )
      }

      const stepThreeResponse = await createProcessService({
        test_selection: {
          process_id: stepOneData.processId as number,
          betesa_test: selectedTests.betesa,
          competences_test: selectedTests.skills,
          test_360: false,
          online_course: false,
        },
      })

      if (stepThreeResponse.status !== 'OK') {
        throw new Error(
          'No pudimos guardar la selección de pruebas del proceso.',
        )
      }

      if (selectedTests.betesa) {
        const betesaResponse = await createProcessService({
          ideal_profile: {
            process_id: stepOneData.processId as number,
            betesa_ideal_profile: {
              betesa_skills: [],
              alert_level_range: alertLevelIds,
              profile_values: builtBetesaProfile,
            },
          },
        })

        if (betesaResponse.status !== 'OK') {
          throw new Error(
            'No pudimos guardar el perfil ideal de Betesa.',
          )
        }
      }

      if (selectedTests.skills) {
        const skillsResponse = await createProcessService({
          ideal_profile: {
            process_id: stepOneData.processId as number,
            competences_ideal_profile: selectedCompetences.map(
              ({ skill_id, weight }) => ({
                skill_id,
                weight,
              }),
            ),
          },
        })

        if (skillsResponse.status !== 'OK') {
          throw new Error(
            'No pudimos guardar el perfil ideal de 3D Skills.',
          )
        }
      }

      const savedProcessResponse = await getProcessDetail(
        stepOneData.processId as number,
      )

      if (savedProcessResponse.status !== 'OK') {
        throw new Error(
          'No pudimos verificar la configuración guardada del proceso.',
        )
      }

      const savedProcess =
        (savedProcessResponse as any).process ||
        savedProcessResponse.data?.process

      if (selectedTests.skills) {
        const expectedSkillIds = selectedCompetences.map(
          ({ skill_id }) => skill_id,
        )
        const savedSkillIds =
          savedProcess?.competences_ideal_profile?.map(
            (item: { skill_id: number }) => item.skill_id,
          ) || []

        if (!haveSameNumericValues(expectedSkillIds, savedSkillIds)) {
          throw new Error(
            'La configuración de 3D Skills no coincide con lo que editaste. No continuaremos hasta que quede guardada correctamente.',
          )
        }
      }

      if (selectedTests.betesa) {
        const savedAlertRange =
          savedProcess?.betesa_ideal_profile?.alert_level_range || []
        const savedProfileValues =
          savedProcess?.betesa_ideal_profile?.profile_values || []

        if (
          !haveSameNumericValues(alertLevelIds, savedAlertRange) ||
          savedProfileValues.length !== builtBetesaProfile.length
        ) {
          throw new Error(
            'La configuración de Betesa no coincide con lo que editaste. No continuaremos hasta que quede guardada correctamente.',
          )
        }
      }

      navigate(platformAdminRoutes.CreateUsers)
    } catch (error) {
      setAllowNavigation(false)
      getError(
        error instanceof Error
          ? error.message
          : 'No pudimos guardar la configuración generada con IA.',
      )
    } finally {
      setLoading(false)
    }
  }

  const handleContinueManual = () => {
    setAllowNavigation(true)
    dispatch(
      setStepTwo({
        processId: stepOneData.processId as number,
        isRoleFamilySelected: false,
        configurationMode: 'manual',
      }),
    )
    dispatch(
      setStepTwoTrigger.run({
        processId: stepOneData.processId as number,
        isRoleFamilySelected: false,
        configurationMode: 'manual',
      }),
    )
    navigate(platformAdminRoutes.SelectTestsType)
  }

  return (
    <MainLayout>
      <Container>
        <HeaderContainer>
          <H2 margin="0 0 0 20px">
            Generador de perfil ideal con IA
          </H2>
          <Stepper activeStep={1} steps={STEPS_NEW_PROCESS} />
        </HeaderContainer>
        <Content>
          <Card>
            <H4>Carga el perfil del cargo en PDF</H4>
            <P>
              Usaremos la lógica de Lumina para interpretar el rol,
              proponer Betesa y 3D Skills por defecto, y dejar la
              sustentación informativa antes de aplicar la sugerencia.
            </P>
            {shouldPreventExit && (
              <ValidationBox $tone="warning" style={{ marginTop: 16 }}>
                <strong>Importante</strong>
                <div style={{ marginTop: 6 }}>
                  Este proceso no guarda borrador de generación con IA
                  automáticamente. Si sales ahora, tendrás que volver a
                  generar y configurar esta sugerencia desde el inicio.
                </div>
              </ValidationBox>
            )}
            <UploadBox
              $isDragging={isDragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <strong>
                {file
                  ? `Archivo seleccionado: ${file.name}`
                  : 'Selecciona o arrastra aquí el PDF del cargo'}
              </strong>
              <span>
                Formato esperado: PDF descriptivo del cargo, con
                funciones, nivel y competencias.
              </span>
              <FileInput
                accept="application/pdf"
                type="file"
                onChange={(event) => {
                  handleFileSelected(event.target.files?.[0] || null)
                  event.target.value = ''
                }}
              />
            </UploadBox>
            <div style={{ marginTop: 20 }}>
              <Button type="button" onClick={handleGenerate}>
                Generar sugerencia
              </Button>
            </div>
          </Card>

          {preview && betesaDraft && (
            <>
              <Card>
                <TestHeader>
                  <div>
                    <H4>Lectura del rol</H4>
                    <MutedText>
                      Revisamos el contenido del PDF y armamos una base
                      editable antes de aplicarla al proceso.
                    </MutedText>
                  </div>
                  <StatusPill $tone={preview.validation.ok ? 'success' : 'warning'}>
                    {preview.validation.ok
                      ? 'Listo para revisar'
                      : 'Requiere ajustes'}
                  </StatusPill>
                </TestHeader>

                <EditorGrid $columns="repeat(3, minmax(0, 1fr))">
                  <div>
                    <EditorLabel>Rol</EditorLabel>
                    <RoleSummary>{displayRoleName}</RoleSummary>
                  </div>
                  <div>
                    <EditorLabel>Tipo de rol</EditorLabel>
                    <RoleSummary>
                      {preview.role.tipo_rol || 'Sin detectar'}
                    </RoleSummary>
                  </div>
                  <div>
                    <EditorLabel>Nivel</EditorLabel>
                    <RoleSummary>
                      {preview.role.nivel || 'Sin detectar'}
                    </RoleSummary>
                  </div>
                </EditorGrid>

                <div style={{ marginTop: 16 }}>
                  <EditorLabel>Resumen del perfil leído</EditorLabel>
                  <RoleSummary>{buildRoleSummary(preview)}</RoleSummary>
                </div>

                <div style={{ marginTop: 16 }}>
                  <EditorLabel>Competencias detectadas</EditorLabel>
                  <TagList>
                    {(preview.role.competencias_clave || []).map((item) => (
                      <Tag key={item}>{item}</Tag>
                    ))}
                  </TagList>
                </div>

                <div style={{ marginTop: 16 }}>
                  <EditorLabel>Demandas del entorno</EditorLabel>
                  <TagList>
                    {(preview.role.demandas_entorno || []).map((item) => (
                      <Tag key={item}>{item}</Tag>
                    ))}
                  </TagList>
                </div>
              </Card>

              <Card>
                <H4>Pruebas recomendadas</H4>
                <MutedText>
                  Lumina recomienda ambas pruebas por defecto. El cliente
                  puede retirar una si no desea aplicarla.
                </MutedText>
                <RecommendationGrid>
                  <RecommendationCard $active={selectedTests.betesa}>
                    <ToggleRow>
                      <div>
                        <strong>Betesa</strong>
                        <MutedText>
                          Base cognitiva y rango de alerta sugerido para
                          el cargo.
                        </MutedText>
                      </div>
                      <Checkbox
                        type="checkbox"
                        checked={selectedTests.betesa}
                        onChange={(
                          event: ChangeEvent<HTMLInputElement>,
                        ) =>
                          setSelectedTests((current) => ({
                            ...current,
                            betesa: event.target.checked,
                          }))
                        }
                      />
                    </ToggleRow>
                  </RecommendationCard>

                  <RecommendationCard $active={selectedTests.skills}>
                    <ToggleRow>
                      <div>
                        <strong>3D Skills</strong>
                        <MutedText>
                          Competencias sugeridas y priorizadas para el
                          proceso.
                        </MutedText>
                      </div>
                      <Checkbox
                        type="checkbox"
                        checked={selectedTests.skills}
                        onChange={(
                          event: ChangeEvent<HTMLInputElement>,
                        ) =>
                          setSelectedTests((current) => ({
                            ...current,
                            skills: event.target.checked,
                          }))
                        }
                      />
                    </ToggleRow>
                  </RecommendationCard>
                </RecommendationGrid>
              </Card>

              {selectedTests.betesa && (
                <Card>
                  <H4>Betesa ideal</H4>
                  <MutedText>
                    Primero revisa la estructura Betesa sugerida. Puedes
                    ajustar el rol de cada cuadrante y su rango ideal.
                  </MutedText>
                  <EditorGrid $columns="repeat(2, minmax(0, 1fr))">
                    {QUADRANTS.map((quadrant) => (
                      <RecommendationCard
                        key={quadrant}
                        $active={selectedTests.betesa}
                        $roleTone={betesaDraft[quadrant].role}
                      >
                        <QuadrantHeader>
                          <QuadrantName>{quadrant}</QuadrantName>
                          <QuadrantBadge
                            $roleTone={betesaDraft[quadrant].role}
                          >
                            {
                              QUADRANT_ROLE_LABELS[
                                betesaDraft[quadrant].role
                              ]
                            }
                          </QuadrantBadge>
                        </QuadrantHeader>
                        <EditorGrid $columns="repeat(3, minmax(0, 1fr))">
                          <div>
                            <EditorLabel>Rol estructural</EditorLabel>
                            <Select
                              value={betesaDraft[quadrant].role}
                              onChange={(
                                event: ChangeEvent<HTMLSelectElement>,
                              ) =>
                                handleQuadrantChange(
                                  quadrant,
                                  'role',
                                  event.target.value,
                                )
                              }
                            >
                              <option value="dominante">
                                Dominante ideal
                              </option>
                              <option value="auxiliar_1">
                                Auxiliar 1
                              </option>
                              <option value="auxiliar_2">
                                Auxiliar 2
                              </option>
                              <option value="menos_eficiente">
                                Menos eficiente
                              </option>
                            </Select>
                          </div>
                          <div>
                            <EditorLabel>Mín.</EditorLabel>
                            <Input
                              type="number"
                              min="0"
                              max="101"
                              value={betesaDraft[quadrant].min}
                              onChange={(
                                event: ChangeEvent<HTMLInputElement>,
                              ) =>
                                handleQuadrantChange(
                                  quadrant,
                                  'min',
                                  event.target.value,
                                )
                              }
                            />
                          </div>
                          <div>
                            <EditorLabel>Máx.</EditorLabel>
                            <Input
                              type="number"
                              min="0"
                              max="101"
                              value={betesaDraft[quadrant].max}
                              onChange={(
                                event: ChangeEvent<HTMLInputElement>,
                              ) =>
                                handleQuadrantChange(
                                  quadrant,
                                  'max',
                                  event.target.value,
                                )
                              }
                            />
                          </div>
                        </EditorGrid>
                      </RecommendationCard>
                    ))}
                  </EditorGrid>

                  <div style={{ marginTop: 20 }}>
                    <EditorLabel>
                      Rango ideal de nivel de alerta
                    </EditorLabel>
                    <Select
                      value={alertRangeValue}
                      onChange={(
                        event: ChangeEvent<HTMLSelectElement>,
                      ) => setAlertRangeValue(event.target.value)}
                    >
                      {ALERT_RANGE_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div style={{ marginTop: 20 }}>
                    <EditorLabel>
                      ¿Porqué Bluesite Lumina te sugiere este perfil para
                      Betesa?
                    </EditorLabel>
                    <InfoBox>{rationales.betesa}</InfoBox>
                  </div>
                </Card>
              )}

              {selectedTests.skills && (
                <Card>
                  <H4>3D Skills sugerido</H4>
                  <MutedText>
                    Después revisa las competencias sugeridas para el
                    proceso. Puedes ajustar la prioridad y el mínimo
                    esperado de cada una.
                  </MutedText>

                  <TableToolbar>
                    <CounterPill
                      $tone={
                        enabledCompetences.length < MIN_SKILLS ||
                        enabledCompetences.length > MAX_SKILLS
                          ? 'warning'
                          : 'default'
                      }
                    >
                      {enabledCompetences.length} competencias activas
                    </CounterPill>

                    <InlineActions>
                      <SearchableSelectWrap>
                        <Input
                          type="text"
                          value={skillSearch}
                          placeholder="Buscar competencia disponible del nivel"
                          onFocus={() => setShowSkillResults(true)}
                          onBlur={() =>
                            window.setTimeout(
                              () => setShowSkillResults(false),
                              120,
                            )
                          }
                          onChange={(
                            event: ChangeEvent<HTMLInputElement>,
                          ) => {
                            setSkillSearch(event.target.value)
                            setShowSkillResults(true)
                            const matchedSkill = availableSkillsToAdd.find(
                              (item) =>
                                normalizeSearchText(item.skill) ===
                                normalizeSearchText(event.target.value),
                            )
                            setNewSkillId(
                              matchedSkill ? String(matchedSkill.id) : '',
                            )
                          }}
                        />
                        {showSkillResults && (
                          <SearchResults>
                            {filteredSkillsToAdd.length > 0 ? (
                              filteredSkillsToAdd.map((item) => (
                                <SearchResultButton
                                  key={item.id}
                                  type="button"
                                  $active={String(item.id) === newSkillId}
                                  onMouseDown={() => {
                                    setNewSkillId(String(item.id))
                                    setSkillSearch(item.skill)
                                    setShowSkillResults(false)
                                  }}
                                >
                                  {item.skill}
                                </SearchResultButton>
                              ))
                            ) : (
                              <SearchEmptyState>
                                No encontramos competencias con esa
                                búsqueda.
                              </SearchEmptyState>
                            )}
                          </SearchResults>
                        )}
                      </SearchableSelectWrap>
                      <SmallButton
                        type="button"
                        onClick={handleAddCompetence}
                      >
                        Agregar
                      </SmallButton>
                    </InlineActions>
                  </TableToolbar>

                  <TableWrap>
                    <StyledTable>
                      <thead>
                        <tr>
                          <th>Competencia</th>
                          <th>Prioridad</th>
                          <th>Mínimo esperado</th>
                          <th>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enabledCompetences.map((item, index) => (
                          <tr key={`${item.skill_name}-${index}`}>
                            <td>{item.skill_name}</td>
                            <td>
                              <Select
                                value={item.priority}
                                onChange={(
                                  event: ChangeEvent<HTMLSelectElement>,
                                ) =>
                                  handleCompetenceChange(
                                    index,
                                    'priority',
                                    event.target.value,
                                  )
                                }
                              >
                                {enabledCompetences.map((_, optionIndex) => (
                                  <option
                                    key={optionIndex + 1}
                                    value={optionIndex + 1}
                                  >
                                    {optionIndex + 1}
                                  </option>
                                ))}
                              </Select>
                            </td>
                            <td>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={item.weight}
                                onChange={(
                                  event: ChangeEvent<HTMLInputElement>,
                                ) =>
                                  handleCompetenceChange(
                                    index,
                                    'weight',
                                    event.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <RemoveButton
                                type="button"
                                onClick={() =>
                                  handleRemoveCompetence(
                                    competenceDrafts.findIndex(
                                      (draft) =>
                                        draft.enabled &&
                                        draft.skill_id === item.skill_id &&
                                        draft.skill_name === item.skill_name,
                                    ),
                                  )
                                }
                              >
                                Quitar
                              </RemoveButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </StyledTable>
                  </TableWrap>

                  <div style={{ marginTop: 20 }}>
                    <EditorLabel>
                      ¿Porqué Bluesite Lumina te sugiere este perfil para
                      3DSkills?
                    </EditorLabel>
                    <InfoBox>{skillsRationale}</InfoBox>
                  </div>
                </Card>
              )}

              {(!preview.validation.ok ||
                preview.validation.warnings?.length > 0) && (
                <Card>
                  <H4>Observaciones de calidad del PDF</H4>
                  {!preview.validation.ok && (
                    <ValidationBox $tone="error">
                      <strong>Faltan elementos clave</strong>
                      <ul>
                        {preview.validation.errors.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </ValidationBox>
                  )}
                  {preview.validation.warnings?.length > 0 && (
                    <ValidationBox $tone="warning">
                      <strong>Advertencias</strong>
                      <ul>
                        {preview.validation.warnings.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </ValidationBox>
                  )}
                </Card>
              )}
            </>
          )}

          <SectionDivider />

          <Footer>
            <BackButton
              onClick={() =>
                handleProtectedNavigate(
                  platformAdminRoutes.PreconfigProcess,
                )
              }
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <StrokedButton
                type="button"
                onClick={handleContinueManual}
              >
                Seguir manualmente
              </StrokedButton>
              <Button
                disabled={!preview || !preview.validation?.ok}
                type="button"
                onClick={handleApply}
              >
                Aplicar sugerencia
              </Button>
            </div>
          </Footer>
        </Content>
      </Container>
      <ModalLoader isLoading={loading} />
      <Modal
        show={showExitModal || isNavigationModalOpen}
        setShow={() => {
          handleCloseExitModal()
          handleCloseNavigationModal()
        }}
        styleContent={{ padding: '2rem 2rem 0rem 2rem' }}
        hideCloseButton
      >
        <H4 color="red">
          Este proceso no guarda borrador de generación con IA
          automáticamente.
        </H4>
        <H3 margin="24px 0px" textAlign="center">
          Si sales ahora, tendrás que volver a generar y configurar esta
          sugerencia desde el inicio.
        </H3>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <StrokedButton
            onClick={() => {
              handleCloseExitModal()
              handleCloseNavigationModal()
            }}
          >
            Continuar aquí
          </StrokedButton>
          <Button
            onClick={() => {
              if (showExitModal) {
                handleConfirmExitModal()
                return
              }
              handleConfirmNavigationModal()
            }}
          >
            Salir de todos modos
          </Button>
        </div>
      </Modal>
    </MainLayout>
  )
}

export default IdealProfileGeneratorAi
